// 10s loop for the "Send payments easily" card (design handoff:
// pay-across-the-world-animation.md). A "Pay" button is pressed on an upcoming
// payment, the UI cross-fades to a rotating dot globe, and a payment arcs from
// New York to Dublin. Pure frame function so the timeline can be unit tested.

export const PAY_LOOP_SECONDS = 10;
export const PAY_START_BALANCE = 152_000;
export const PAY_AMOUNT = 24_500;
/** Reduced-motion visitors see the static UI layer (t=0). */
export const PAY_REST_SECONDS = 0;

const GLOBE_IN = 2.5;
const GLOBE_OUT = 8.8;

const clamp = (x: number) => Math.min(1, Math.max(0, x));
const ease = (x: number) => 1 - (1 - clamp(x)) ** 3;
const easeInOut = (x: number) => {
  const c = clamp(x);
  return c < 0.5 ? 4 * c * c * c : 1 - (-2 * c + 2) ** 3 / 2;
};

export interface PayFrame {
  balance: number;
  pressed: boolean;
  cursor: { x: number; y: number; opacity: number };
  globeVisible: boolean;
  /** Globe rotation in radians (only advances while the globe is visible). */
  rotation: number;
  /** Route draw fraction (0 to 1). */
  arc: number;
  packetVisible: boolean;
  sourcePulse: number;
  destinationPulse: number;
  destinationLit: boolean;
  sourceChipVisible: boolean;
  destinationChipVisible: boolean;
  confirmationVisible: boolean;
}

export function getPayFrame(elapsedSeconds: number): PayFrame {
  const t = elapsedSeconds % PAY_LOOP_SECONDS;
  const k = easeInOut((t - 0.8) / 1.2);
  const pressed = t >= 2.1 && t < 2.3;
  const globeVisible = t >= GLOBE_IN && t < GLOBE_OUT;
  const balance =
    t >= 9.2
      ? PAY_START_BALANCE
      : t >= 2.1
        ? PAY_START_BALANCE - ease((t - 2.2) / 1.2) * PAY_AMOUNT
        : PAY_START_BALANCE;

  return {
    balance,
    pressed,
    cursor: {
      x: 80 * (1 - k),
      y: 60 * (1 - k) + (pressed ? 2 : 0),
      opacity: t < 0.8 ? 0 : t < 1.1 ? (t - 0.8) / 0.3 : t < GLOBE_IN ? 1 : 0,
    },
    globeVisible,
    rotation: 0.62 + (globeVisible ? (t - GLOBE_IN) * 0.035 : 0),
    arc: ease((t - 3) / 2.4),
    packetVisible: t >= 3 && t < 5.5,
    sourcePulse: clamp((t - 3) / 0.9),
    destinationPulse: clamp((t - 5.4) / 0.9),
    destinationLit: t >= 5.3,
    sourceChipVisible: t >= 2.9,
    destinationChipVisible: t >= 5.5,
    confirmationVisible: t >= 6.2,
  };
}
